import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";

import { useParams, useRouteMatch } from "react-router-dom";

import {
  getSearchMentionsV4,
  getTagMentionsV4,
} from "@/api/endpoints/searches";
import { postTrackingData } from "@/api/endpoints/track";
import { useAlert } from "@/contexts/AlertContext";
import MentionCardsContext from "@/contexts/MentionCardsContext";
import { useTags } from "@/contexts/TagContext";
import { useFilterParamsV4 } from "@/hooks/useFilterParams";
import { ROUTES } from "@/routes_new";
import { EVENT } from "@/tracking_events";
import { MentionCard, MentionV4 } from "@/types/MentionCard";
import { PaginatedMentionCards } from "@/types/PaginatedMentionCards";
import { Search } from "@/types/Search";
import { userAgentIsPhonelike } from "@/utils";

const actionTypes = {
  // Episodes are the full objects
  SET_MENTION_CARDS: "set_episode",
  PATCH_MENTION_CARD: "patch_episode",
  DELETE_MENTION_CARD: "delete_episode",
  // Mention refers to a single Mention
  UPDATE_MENTION: "update_mention",
  PATCH_MENTION: "patch_mention",
} as const;

type ReducerAction =
  | {
      type: "set_episode";
      payload: MentionCard[];
    }
  | {
      type: "patch_episode";
      payload: Partial<MentionCard> &
        Required<Pick<MentionCard["episode"], "id">>;
    }
  | { type: "delete_episode"; payload: string }
  | { type: "update_mention"; payload: MentionV4 }
  | { type: "patch_mention"; payload: MentionV4 };

function reducer(
  state: { mentionCards: MentionCard[] },
  action: ReducerAction
): { mentionCards: MentionCard[] } {
  switch (action.type) {
    case actionTypes.SET_MENTION_CARDS:
      return { mentionCards: action.payload };
    case actionTypes.UPDATE_MENTION:
      return {
        mentionCards: state.mentionCards.map((mentionCard) => {
          return {
            // Nothing changes in the mention card itself.
            ...mentionCard,
            // Find the mention to be updated
            mentions: mentionCard.mentions.map((mention) =>
              mention.public_id === action.payload.public_id
                ? action.payload
                : mention
            ),
          };
        }),
      };
    case actionTypes.PATCH_MENTION:
      return {
        mentionCards: state.mentionCards.map((mentionCard) => {
          return {
            // Nothing changes in the mention card itself.
            ...mentionCard,
            // Find the mention to be updated
            mentions: mentionCard.mentions.map((mention) =>
              mention.public_id === action.payload.public_id
                ? { ...mention, ...action.payload }
                : mention
            ),
          };
        }),
      };

    case actionTypes.PATCH_MENTION_CARD:
      return {
        mentionCards: state.mentionCards.map((mentionCard) => {
          return action.payload.id === mentionCard.episode.id
            ? {
                ...mentionCard,
                ...action.payload,
              }
            : mentionCard;
        }),
      };

    case actionTypes.DELETE_MENTION_CARD:
      return {
        mentionCards: state.mentionCards.filter(
          (mentionCard) => action.payload !== mentionCard.episode.id
        ),
      };
    default:
      throw Error("Unknown action.");
  }
}

const MentionCardsProvider = ({
  children,
  hideActions,
  hideTags,
  hideReach,
  hideSentiment,
  hidePrValue,
  overrideSlug,
  searchUuid,
  orgUuid,
  startDate,
}: {
  children: ReactNode;
  hideActions?: boolean;
  hideTags?: boolean;
  hideReach?: boolean;
  hideSentiment?: boolean;
  hidePrValue?: boolean;
  overrideSlug?: string;
  searchUuid?: string;
  orgUuid?: Uuid;
  startDate?: Date;
}) => {
  const [state, dispatch] = useReducer(reducer, {
    mentionCards: [],
  });
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState<Pick<
    PaginatedMentionCards,
    "page_count" | "count"
  > | null>(null);
  const { organizationTags } = useTags();
  const { uuid = searchUuid, slug: paramSlug } = useParams<{
    slug?: string;
    uuid?: string;
  }>();

  const slug = paramSlug || overrideSlug;

  const [errors, setErrors] = useState<Error[]>([]);
  const [filters] = useFilterParamsV4();
  const { alerts } = useAlert();
  // Data for the share modal
  const [shareMention, setShareMention] = useState<MentionCard | null>(null);
  const isFavoritePage = Boolean(useRouteMatch(ROUTES.FAVORITES_ORGID));

  // alert for alert view:
  const activeAlert = useMemo<Search | null>(
    () => alerts?.find((a) => a.uuid === uuid) || null,
    [alerts, uuid]
  );

  const activeTag = useMemo(
    // Rare use of loose equal here to allow the slug to be a number.
    () => {
      if (isFavoritePage)
        return organizationTags.find((t) => t.slug == "favorited");
      return organizationTags.find((t) => t.slug == slug);
    },
    [slug, organizationTags, isFavoritePage]
  );

  const setMentionCards = useCallback(
    (mentionCards: MentionCard[]) =>
      dispatch({ type: actionTypes.SET_MENTION_CARDS, payload: mentionCards }),
    []
  );

  const updateMention = useCallback(
    (mention: MentionCard["mentions"][0]) =>
      dispatch({ type: actionTypes.UPDATE_MENTION, payload: mention }),
    []
  );

  const patchMentionCard = useCallback(
    (mentionCardWithEpisodeId: Partial<MentionCard> & { id: string }) =>
      dispatch({
        type: actionTypes.PATCH_MENTION_CARD,
        payload: mentionCardWithEpisodeId,
      }),
    []
  );

  const deleteMentionCard = useCallback(
    (episodeUuid: MentionCard["episode"]["id"]) =>
      dispatch({ type: actionTypes.DELETE_MENTION_CARD, payload: episodeUuid }),
    []
  );

  const share = useCallback(async (mentionCard: MentionCard) => {
    // Either opens a share modal, or the built-in share context
    // on for example a phone or tablet
    postTrackingData(EVENT.SHARE_MENTION, {
      url: mentionCard.share_url,
      source: mentionCard.episode.audio_url,
    });

    if (navigator && navigator.share && userAgentIsPhonelike()) {
      try {
        // share using the share context
        await navigator.share({
          title: `${mentionCard.episode.title} - All Ears`,
          url: mentionCard.share_url,
        });
      } catch (err) {
        // An error is thrown when the user aborts the dialog

        if (!(err as DOMException).name?.includes?.("AbortError")) {
          // fallback to using the regular modal
          setShareMention(mentionCard);
        }
      }
    } else {
      setShareMention(mentionCard);
    }
  }, []);

  const shareV4 = useCallback(async (mentionCard: MentionCard) => {
    // Either opens a share modal, or the built-in share context
    // on for example a phone or tablet
    const {
      share_url,
      episode: { audio_url, title },
    } = mentionCard;

    postTrackingData(EVENT.SHARE_MENTION, {
      url: share_url,
      source: audio_url,
    });

    if (navigator && navigator.share && userAgentIsPhonelike()) {
      try {
        // share using the share context
        await navigator.share({
          title: `${title} - All Ears`,
          url: share_url,
        });
      } catch (err) {
        // An error is thrown when the user aborts the dialog

        if (!(err as DOMException).name?.includes?.("AbortError")) {
          // fallback to using the regular modal
          setShareMention(mentionCard);
        }
      }
    } else {
      setShareMention(mentionCard);
    }
  }, []);

  const closeShare = () => {
    setShareMention(null);
  };

  // Get mention data
  useEffect(() => {
    let mounted = true;

    if (uuid) {
      setLoading(true);
      setMentionCards([]);
      setErrors([]);
      setMeta(null);
      getSearchMentionsV4(uuid, filters, startDate)
        .then((response) => {
          if (!response.ok) throw new Error(response.statusText);
          if (!mounted) return;

          const {
            page_count,
            count,
            results: mentionCards,
          } = response.data as {
            page_count: number;
            count: number;
            results: MentionCard[];
          };

          setMeta({ page_count, count });
          setMentionCards(mentionCards);
          setLoading(false);
        })
        .catch((e) => {
          setLoading(false);
          // Checking mounted here will make sure we're not showing an error message when a user is simply leaving the view.
          mounted && setErrors([e]);
        });
    } else if (slug || isFavoritePage) {
      setLoading(true);
      getTagMentionsV4(slug || "favorited", filters, orgUuid).then(
        ({ data, ok }) => {
          if (!mounted) return;
          if (ok && data) {
            setMentionCards(data.results);
            setMeta({ page_count: data.page_count, count: data.count });
            setLoading(false);
          }
        }
      );
    }

    return () => {
      mounted = false;
    };
  }, [
    filters,
    slug,
    orgUuid,
    setMentionCards,
    isFavoritePage,
    uuid,
    startDate,
  ]);

  return (
    <MentionCardsContext.Provider
      value={{
        mentionCards: state.mentionCards,
        loading,
        meta,
        errors,
        tag: activeTag,
        alert: activeAlert,
        shareMention,
        share,
        shareV4,
        closeShare,
        updateMention,
        setMentionCards,
        deleteMentionCard: deleteMentionCard,
        patchMentionCard: patchMentionCard,
        hideActions,
        hideTags,
        hideSentiment,
        hideReach,
        hidePrValue,
      }}
    >
      {children}
    </MentionCardsContext.Provider>
  );
};
export default MentionCardsProvider;
